import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCategories } from '@/contexts/CategoriesContext';
import { useTranslation } from '@/hooks/useTranslation';
import { QuickAddTransaction } from '@/components/QuickAddTransaction';
import { getYearSummary } from '@/lib/firestore';
import {
  getYearMonthlySavings,
  getYearExpensesByCategory,
} from '@/lib/firestore-charts';
import {
  getAllYearTransactions,
  getYearMonthlySummary,
} from '@/lib/firestore-export';
import { exportToCSV, exportCompleteReport } from '@/lib/export-utils';
import {
  filterTransactions,
  getFilterStats,
  type FilterCriteria,
} from '@/lib/filter-utils';
import { formatMoney } from '@/lib/utils';
import { Settings as SettingsIcon, Repeat, MapPin } from 'lucide-react';
import { SavingsLineChart } from '@/components/charts/SavingsLineChart';
import { ExpensePieChart } from '@/components/charts/ExpensePieChart';
import { ExportButton } from '@/components/ExportButton';
import { SearchFilterBar } from '@/components/SearchFilterBar';
import { YearlyGoalCard } from '@/components/YearlyGoalCard';
import { OnboardingWizard } from '@/components/OnboardingWizard';
import { FeedbackButton } from '@/components/FeedbackButton';
import { TripModal } from '@/components/TripModal';
import type { Transaction } from '@/types';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function Dashboard() {
  const { user, logout, isNewUser, setIsNewUser } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getCategoryLabel } = useCategories();

  const [summary, setSummary] = useState({
    totalSavings: 0,
    totalIncome: 0,
    totalExpenses: 0,
  });
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<
    Transaction[]
  >([]);
  const [savingsData, setSavingsData] = useState<
    Array<{ month: string; savings: number }>
  >([]);
  const [expensesData, setExpensesData] = useState<
    Array<{ categoryId: string; amount: number }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showTrip, setShowTrip] = useState(false);

  // Filter state
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({
    searchQuery: '',
    type: 'all',
    categoryId: null,
    dateRange: null,
  });

  const currentYear = new Date().getFullYear();
  const currency = settings?.currency || 'USD';
  const yearlyGoal = settings?.yearlyGoal || 0;

  const loadDashboardData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [yearData, allTrans, savings, expenses] = await Promise.all([
        getYearSummary(user.uid, currentYear),
        getAllYearTransactions(user.uid, currentYear),
        getYearMonthlySavings(user.uid, currentYear),
        getYearExpensesByCategory(user.uid, currentYear),
      ]);

      setSummary(yearData);
      setAllTransactions(allTrans);
      setFilteredTransactions(allTrans);
      setSavingsData(savings);
      setExpensesData(expenses);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [user, currentYear]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Apply filters whenever criteria changes
  useEffect(() => {
    const filtered = filterTransactions(allTransactions, filterCriteria);
    setFilteredTransactions(filtered);
  }, [allTransactions, filterCriteria]);

  // Filter handlers
  const handleSearch = (query: string) => {
    setFilterCriteria((prev) => ({ ...prev, searchQuery: query }));
  };

  const handleFilterType = (type: 'all' | 'gain' | 'expense') => {
    setFilterCriteria((prev) => ({ ...prev, type, categoryId: null }));
  };

  const handleFilterCategory = (categoryId: string | null) => {
    setFilterCriteria((prev) => ({ ...prev, categoryId }));
  };

  const handleFilterDateRange = (
    dateRange: { start: Date; end: Date } | null
  ) => {
    setFilterCriteria((prev) => ({ ...prev, dateRange }));
  };

  // Export handlers
  const handleExportAllCSV = async () => {
    if (!user) return;
    setExporting(true);
    try {
      const transactions = await getAllYearTransactions(user.uid, currentYear);
      exportToCSV(
        transactions,
        `mybalance-${currentYear}-all-transactions.csv`
      );
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert(t('errors.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCompleteReport = async () => {
    if (!user) return;
    setExporting(true);
    try {
      const [monthlyData, transactions] = await Promise.all([
        getYearMonthlySummary(user.uid, currentYear),
        getAllYearTransactions(user.uid, currentYear),
      ]);
      exportCompleteReport(
        monthlyData,
        transactions,
        currentYear,
        `mybalance-report-${currentYear}.xlsx`
      );
    } catch (error) {
      console.error('Error exporting report:', error);
      alert(t('errors.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const filterStats = getFilterStats(filteredTransactions);

  return (
    <div className="fixed inset-0 flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      {/* Fixed Header */}
      <header className="sticky top-0 z-50 w-full flex-shrink-0 border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/95">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between gap-2 sm:h-16">
            {/* Logo & Title */}
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 sm:h-10 sm:w-10 sm:rounded-xl">
                <span className="text-lg sm:text-xl">💰</span>
              </div>
              <h1 className="truncate bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-base font-bold text-transparent sm:text-xl md:text-2xl">
                {t('dashboard.title')}
              </h1>
            </div>

            {/* Header Actions */}
            <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
              {/* User Greeting - Desktop Only */}
              <div className="mr-2 hidden text-right lg:block">
                <p className="max-w-[150px] truncate text-xs font-medium text-slate-700 dark:text-slate-300 sm:text-sm">
                  {t('auth.welcomeBack')},{' '}
                  {user?.email?.split('@')[0] || 'User'}!
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 sm:text-xs">
                  {user?.email}
                </p>
              </div>

              {/* Recurring Button */}
              <button
                onClick={() => navigate('/recurring')}
                className="flex min-h-[36px] items-center gap-1 rounded-lg bg-slate-100 px-2 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:bg-slate-200 active:scale-95 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 sm:min-h-[44px] sm:gap-2 sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
                title={t('nav.recurring')}
              >
                <Repeat className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden md:inline">{t('nav.recurring')}</span>
              </button>

              {/* New Group Trip Button */}
              <button
                onClick={() => setShowTrip(true)}
                className="flex min-h-[36px] items-center gap-1 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-600 px-2 py-1.5 text-xs font-semibold text-white shadow-md transition-all hover:from-violet-600 hover:to-fuchsia-700 hover:shadow-lg active:scale-95 sm:min-h-[44px] sm:gap-2 sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
                title="New Group Trip"
              >
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden md:inline">Group Trip</span>
              </button>

              {/* Export Button */}
              <div className="hidden sm:block">
                <ExportButton
                  onExportCSV={handleExportAllCSV}
                  onExportExcel={handleExportCompleteReport}
                  label={t('common.export')}
                  variant="compact"
                />
              </div>

              {/* Settings Button */}
              <button
                onClick={() => navigate('/settings')}
                className="flex min-h-[36px] items-center gap-1 rounded-lg bg-slate-100 px-2 py-1.5 text-xs font-semibold text-slate-700 transition-all hover:bg-slate-200 active:scale-95 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 sm:min-h-[44px] sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm"
                aria-label="Settings"
              >
                <SettingsIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden lg:inline">{t('nav.settings')}</span>
              </button>

              {/* Logout Button */}
              <button
                onClick={logout}
                className="min-h-[36px] rounded-lg bg-gradient-to-r from-red-500 to-pink-600 px-2 py-1.5 text-xs font-semibold text-white shadow-lg transition-all hover:from-red-600 hover:to-pink-700 hover:shadow-xl active:scale-95 sm:min-h-[44px] sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm md:px-4"
              >
                <span className="hidden sm:inline">{t('auth.logout')}</span>
                <span className="sm:hidden">Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Main Content */}
      <main className="w-full flex-1 overflow-y-auto overflow-x-hidden">
        <div className="min-h-full w-full">
          <div className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8">
            {/* Page Title */}
            <div className="mb-4 sm:mb-6 md:mb-8">
              <h2 className="mb-1 text-2xl font-bold text-slate-900 dark:text-slate-100 sm:mb-2 sm:text-3xl md:text-4xl">
                📊 {t('nav.dashboard')}
              </h2>
              <p className="text-xs text-slate-600 dark:text-slate-400 sm:text-sm md:text-base">
                {t('dashboard.subtitle')}
              </p>
            </div>

            {loading ? (
              <div className="py-12 text-center sm:py-16 md:py-20">
                <div className="mb-4 animate-pulse text-4xl sm:text-5xl md:text-6xl">
                  💰
                </div>
                <p className="text-base text-slate-600 dark:text-slate-400 sm:text-lg md:text-xl">
                  {t('common.loading')}...
                </p>
              </div>
            ) : (
              <div className="space-y-4 pb-8 sm:space-y-6 md:space-y-8">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-6 lg:grid-cols-3">
                  {/* Savings Card */}
                  <div className="rounded-lg border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur-xl transition-all hover:shadow-2xl dark:border-slate-700 dark:bg-slate-800/80 sm:rounded-xl sm:p-5 sm:shadow-xl md:rounded-2xl md:p-6">
                    <div className="mb-3 flex items-center justify-between sm:mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 sm:h-12 sm:w-12 sm:rounded-xl">
                        <span className="text-xl sm:text-2xl">💰</span>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 sm:px-3 sm:py-1 sm:text-xs">
                        {t('dashboard.savings')}
                      </span>
                    </div>
                    <p className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-400 sm:text-sm">
                      {t('dashboard.totalSavings')}
                    </p>
                    <p className="break-words text-xl font-bold text-slate-900 dark:text-slate-100 sm:text-2xl md:text-3xl">
                      {formatMoney(summary.totalSavings, currency)}
                    </p>
                  </div>

                  {/* Income Card */}
                  <div className="rounded-lg border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur-xl transition-all hover:shadow-2xl dark:border-slate-700 dark:bg-slate-800/80 sm:rounded-xl sm:p-5 sm:shadow-xl md:rounded-2xl md:p-6">
                    <div className="mb-3 flex items-center justify-between sm:mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 sm:h-12 sm:w-12 sm:rounded-xl">
                        <span className="text-xl sm:text-2xl">📈</span>
                      </div>
                      <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 sm:px-3 sm:py-1 sm:text-xs">
                        {t('dashboard.income')}
                      </span>
                    </div>
                    <p className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-400 sm:text-sm">
                      {t('dashboard.totalIncome')}
                    </p>
                    <p className="break-words text-xl font-bold text-slate-900 dark:text-slate-100 sm:text-2xl md:text-3xl">
                      {formatMoney(summary.totalIncome, currency)}
                    </p>
                  </div>

                  {/* Expenses Card */}
                  <div className="rounded-lg border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur-xl transition-all hover:shadow-2xl dark:border-slate-700 dark:bg-slate-800/80 sm:col-span-2 sm:rounded-xl sm:p-5 sm:shadow-xl md:rounded-2xl md:p-6 lg:col-span-1">
                    <div className="mb-3 flex items-center justify-between sm:mb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-red-500 to-pink-600 sm:h-12 sm:w-12 sm:rounded-xl">
                        <span className="text-xl sm:text-2xl">💳</span>
                      </div>
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600 dark:bg-red-900/20 dark:text-red-400 sm:px-3 sm:py-1 sm:text-xs">
                        {t('dashboard.expenses')}
                      </span>
                    </div>
                    <p className="mb-1 text-xs font-medium text-slate-600 dark:text-slate-400 sm:text-sm">
                      {t('dashboard.totalExpenses')}
                    </p>
                    <p className="break-words text-xl font-bold text-slate-900 dark:text-slate-100 sm:text-2xl md:text-3xl">
                      {formatMoney(summary.totalExpenses, currency)}
                    </p>
                  </div>
                </div>

                {/* Yearly Goal Card */}
                <YearlyGoalCard
                  yearlyGoal={yearlyGoal}
                  totalSavings={summary.totalSavings}
                  currency={currency}
                />

                {/* Charts Section */}
                <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
                  <SavingsLineChart data={savingsData} currency={currency} />
                  <ExpensePieChart data={expensesData} currency={currency} />
                </div>

                {/* Search & Filter Bar */}
                <SearchFilterBar
                  onSearch={handleSearch}
                  onFilterCategory={handleFilterCategory}
                  onFilterType={handleFilterType}
                  onFilterDateRange={handleFilterDateRange}
                />

                {/* Filtered Transactions Results */}
                <div className="rounded-lg border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80 sm:rounded-xl sm:p-5 sm:shadow-xl md:rounded-2xl md:p-6">
                  <div className="mb-3 flex flex-col gap-2 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 sm:text-lg">
                      📋 {t('dashboard.transactions')} ({filterStats.count})
                    </h3>
                    {filterStats.count < allTransactions.length && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
                        {t('filter.showing')} {filterStats.count}{' '}
                        {t('dashboard.of')} {allTransactions.length}
                      </span>
                    )}
                  </div>

                  {/* Filter Stats */}
                  {filterStats.count > 0 && (
                    <div className="mb-3 grid grid-cols-3 gap-2 rounded-lg bg-slate-50 p-3 dark:bg-slate-700/50 sm:mb-4 sm:gap-4 sm:rounded-xl sm:p-4">
                      <div>
                        <p className="text-[10px] text-slate-600 dark:text-slate-400 sm:text-xs">
                          {t('dashboard.income')}
                        </p>
                        <p className="break-words text-sm font-bold text-emerald-600 sm:text-base md:text-lg">
                          {formatMoney(filterStats.totalIncome, currency)}
                        </p>
                        <p className="text-[10px] text-slate-500 sm:text-xs">
                          {filterStats.incomeCount}{' '}
                          {t('dashboard.transactions').toLowerCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-600 dark:text-slate-400 sm:text-xs">
                          {t('dashboard.expenses')}
                        </p>
                        <p className="break-words text-sm font-bold text-red-600 sm:text-base md:text-lg">
                          {formatMoney(filterStats.totalExpenses, currency)}
                        </p>
                        <p className="text-[10px] text-slate-500 sm:text-xs">
                          {filterStats.expenseCount}{' '}
                          {t('dashboard.transactions').toLowerCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-600 dark:text-slate-400 sm:text-xs">
                          {t('filter.net')}
                        </p>
                        <p
                          className={`break-words text-sm font-bold sm:text-base md:text-lg ${filterStats.netSavings >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                        >
                          {formatMoney(filterStats.netSavings, currency)}
                        </p>
                      </div>
                    </div>
                  )}

                  {filteredTransactions.length === 0 ? (
                    <div className="py-8 text-center sm:py-12">
                      <p className="text-sm text-slate-500 dark:text-slate-400 sm:text-base">
                        {t('dashboard.noDataYet')}
                      </p>
                      <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 sm:text-sm">
                        {t('dashboard.startTracking')}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-[300px] space-y-2 overflow-y-auto pr-1 sm:max-h-[400px] sm:space-y-3 sm:pr-2 md:max-h-[500px]">
                      {filteredTransactions.map((transaction) => (
                        <div
                          key={transaction.id}
                          className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-2.5 transition-colors hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-700 sm:gap-3 sm:rounded-xl sm:p-3 md:p-4"
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                            <span className="flex-shrink-0 text-base sm:text-lg">
                              {transaction.type === 'gain' ? '💰' : '💳'}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-semibold text-slate-900 dark:text-slate-100 sm:text-sm md:text-base">
                                {transaction.name}
                              </p>
                              <p className="truncate text-[10px] text-slate-500 dark:text-slate-400 sm:text-xs">
                                {getCategoryLabel(transaction.categoryId)} •{' '}
                                {transaction.date.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div
                            className={`ml-2 flex-shrink-0 text-right text-xs font-bold sm:text-sm md:text-base ${transaction.type === 'gain' ? 'text-emerald-600' : 'text-red-600'}`}
                          >
                            {transaction.type === 'gain' ? '+' : '-'}
                            {formatMoney(transaction.amount, currency)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Access to Months */}
                <div className="rounded-lg border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80 sm:rounded-xl sm:p-5 sm:shadow-xl md:rounded-2xl md:p-6">
                  <h3 className="mb-3 text-base font-bold text-slate-900 dark:text-slate-100 sm:mb-4 sm:text-lg">
                    📅 {t('dashboard.quickAccess')}
                  </h3>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3 md:grid-cols-6 lg:grid-cols-12">
                    {MONTHS.map((month) => (
                      <button
                        key={month}
                        onClick={() => navigate(`/month/${month}`)}
                        className="flex min-h-[44px] items-center justify-center rounded-lg bg-slate-50 px-2 py-2 text-center text-xs font-semibold text-slate-700 transition-all hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 hover:text-white active:scale-95 dark:bg-slate-700/50 dark:text-slate-300 sm:rounded-xl sm:px-3 sm:py-2.5 sm:text-sm md:px-4 md:py-3 md:text-base"
                      >
                        {month}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Section */}
                <div className="rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 p-4 text-white shadow-xl sm:rounded-xl sm:p-6 sm:shadow-2xl md:rounded-2xl md:p-8">
                  <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
                    <div className="flex-1">
                      <h3 className="mb-2 text-xl font-bold sm:text-2xl">
                        {t('dashboard.readyToTrack')}
                      </h3>
                      <p className="mb-4 text-xs text-blue-100 sm:mb-6 sm:text-sm md:text-base">
                        {t('dashboard.readyMessage')}
                      </p>
                      <button
                        onClick={() =>
                          navigate(`/month/${MONTHS[new Date().getMonth()]}`)
                        }
                        className="min-h-[44px] rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-lg transition-all hover:bg-blue-50 active:scale-95 sm:rounded-xl sm:px-6 sm:py-3 sm:text-base"
                      >
                        {t('dashboard.goToCurrentMonth')}
                      </button>
                    </div>
                    <div className="hidden text-6xl opacity-20 md:block lg:text-8xl">
                      💰
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Quick Add Transaction FAB */}
      <QuickAddTransaction onSuccess={loadDashboardData} />

      {/* Beta Feedback Button */}
      <FeedbackButton />

      {/* Onboarding Wizard for new users */}
      {isNewUser && (
        <div className="fixed inset-0 z-[200]">
          <OnboardingWizard onComplete={() => setIsNewUser(false)} />
        </div>
      )}

      {/* Trip Modal */}
      {showTrip && <TripModal onClose={() => setShowTrip(false)} />}

      {/* Exporting Modal */}
      {exporting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl dark:bg-slate-800 sm:rounded-2xl sm:p-8">
            <div className="text-center">
              <div className="mb-4 animate-pulse text-4xl sm:text-5xl md:text-6xl">
                📊
              </div>
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100 sm:text-xl">
                {t('export.exporting')}
              </p>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400 sm:text-sm">
                {t('export.exportingMessage')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
